// src/components/AgendaSlot.js
import React from 'react';
import MarcaRecordatorio from './agenda/MarcaRecordatorio';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import {
    Visibility as ViewIcon,
    Payments as CashIcon,
    MedicalServices as ClinicalIcon,
    Add as AddIcon,
    NotInterested as AbsentIcon, // 🚀 AÑADIDO EL ICONO DE AUSENTE
    EventBusy as CancelarCitaIcon
} from '@mui/icons-material';

const AgendaSlot = ({
                        hora,
                        medicoId,
                        cita,
                        fechaBase,
                        pacienteSeleccionado,
                        onAbrirEstacion,
                        onContextMenu,
                        onVerExpediente,
                        onCobrarConsulta,
                        onMarcarAusente, // 🚀 AÑADIDA ESTA PROP PARA EL BOTÓN RÁPIDO
                        onCancelarCita
                    }) => {

    const slotId = `${hora}_${medicoId}`;
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: slotId,
        disabled: Boolean(cita)
    });

    const citaId = cita?.id || cita?._id;
    const {
        attributes,
        listeners,
        setNodeRef: setDraggableRef,
        isDragging
    } = useDraggable({
        id: citaId || 'empty',
        disabled: !cita
    });

    const getEstiloPorEstado = (estado) => {
        switch (estado) {
            case 'en_curso':
                return 'bg-blue-50/90 border-blue-300 text-blue-900 ring-blue-500/20';
            case 'completada':
                return 'bg-emerald-50/90 border-emerald-200 text-emerald-900 ring-emerald-500/10';
            case 'ausente':
                return 'bg-rose-50/80 border-rose-200 text-rose-900 ring-rose-500/10';
            case 'pendiente':
            default:
                return 'bg-amber-50/90 border-amber-200 text-amber-900 ring-amber-500/10';
        }
    };

    const calcularTemporalidadSlot = () => {
        const [horas, minutos] = hora.split(':');
        const fechaSlot = new Date(fechaBase);
        fechaSlot.setHours(parseInt(horas, 10), parseInt(minutos, 10), 0, 0);

        const ahora = new Date();

        const hoyCero = new Date();
        hoyCero.setHours(0, 0, 0, 0);
        const fechaBaseCero = new Date(fechaBase);
        fechaBaseCero.setHours(0, 0, 0, 0);

        if (fechaBaseCero < hoyCero) return 'pasado';
        if (fechaBaseCero > hoyCero) return 'futuro';

        const finSlot = new Date(fechaSlot);
        finSlot.setMinutes(finSlot.getMinutes() + 30);

        if (ahora >= fechaSlot && ahora < finSlot) {
            return 'presente';
        } else if (ahora > finSlot) {
            return 'pasado';
        } else {
            return 'futuro';
        }
    };

    const temporalidad = calcularTemporalidadSlot();

    const getEstiloSlotLibre = (tipo) => {
        switch (tipo) {
            case 'pasado':
                return 'bg-slate-100/40 border-slate-200/60 border-solid text-slate-400 hover:bg-slate-100/80 cursor-not-allowed';
            case 'presente':
                return 'bg-blue-50/60 border-blue-300 border-solid text-blue-500 ring-2 ring-blue-500/15 hover:bg-blue-100/60 hover:border-blue-400';
            case 'futuro':
            default:
                return 'bg-white border-slate-200/80 border-dashed text-slate-400 hover:text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/20 shadow-sm';
        }
    };

    const getBadgeSlotLibre = (tipo) => {
        switch (tipo) {
            case 'pasado':
                return <span className="text-[9px] font-black tracking-widest uppercase text-slate-500 select-none">Pasado</span>;
            case 'presente':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black bg-blue-100 text-blue-700 tracking-widest uppercase select-none animate-pulse">
                        <span className="w-1 h-1 rounded-full bg-blue-500"></span> Ahora
                    </span>
                );
            case 'futuro':
            default:
                return <span className="text-[9px] font-black tracking-widest uppercase text-slate-500 group-hover:text-emerald-600 transition-colors select-none">Disponible</span>;
        }
    };

    if (cita) {
        const estado = cita.estado || 'pendiente';
        const paciente = cita.paciente || {};
        const esPacienteBuscado = pacienteSeleccionado && String(paciente.id || paciente._id) === String(pacienteSeleccionado.id || pacienteSeleccionado._id);

        const calcularEdadCita = (fechaNac) => {
            if (!fechaNac) return '';
            try {
                const cumple = new Date(fechaNac);
                const hoy = new Date();
                let edad = hoy.getFullYear() - cumple.getFullYear();
                const m = hoy.getMonth() - cumple.getMonth();
                if (m < 0 || (m === 0 && hoy.getDate() < cumple.getDate())) {
                    edad--;
                }
                return `${edad} años`;
            } catch {
                return '';
            }
        };

        const edadFormateada = calcularEdadCita(paciente.fechaNacimiento);
        const seguro = paciente.seguroMedico || 'Público';

        return (
            <div
                ref={setDraggableRef}
                {...listeners}
                {...attributes}
                role="button"
                tabIndex={0}
                onClick={() => onAbrirEstacion && onAbrirEstacion(cita)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAbrirEstacion && onAbrirEstacion(cita);
                    }
                }}
                className={`w-full h-full p-2.5 rounded-xl border flex flex-col justify-between shadow-sm cursor-grab active:cursor-grabbing transition-all ring-1 text-left select-none relative group ${getEstiloPorEstado(estado)} ${
                    isDragging ? 'opacity-40 scale-95 border-dashed shadow-none' : ''
                } ${esPacienteBuscado ? 'ring-4 ring-clinica-primary animate-pulse' : ''}`}
            >
                <div className="flex justify-between items-start gap-2">
                    <div className="overflow-hidden flex-1">
                        <h4 className="text-xs font-black truncate tracking-tight uppercase text-slate-900">
                            {paciente.nombre} {paciente.apellidos}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            {edadFormateada && (
                                <span className="text-[9px] font-black bg-slate-900/5 px-1 py-0.2 rounded text-slate-700">
                                    {edadFormateada}
                                </span>
                            )}
                            <span className="text-[9px] font-bold opacity-60 tracking-wider font-mono">
                                {paciente.dni || '---'}
                            </span>
                        </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full shrink-0 mt-1 ${
                        estado === 'en_curso' ? 'bg-blue-500 animate-pulse' :
                            estado === 'completada' ? 'bg-emerald-500' :
                                estado === 'ausente' ? 'bg-rose-500' : 'bg-amber-400'
                    }`} />
                </div>

                <div className="my-1.5 flex-1 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-slate-800 line-clamp-2 leading-tight bg-white/40 p-1 rounded border border-slate-900/5">
                        <span className="opacity-50 font-medium">Motivo: </span>
                        {cita.motivo || 'Consulta médica ordinaria'}
                    </p>
                </div>

                <div className="flex items-center justify-between gap-1 mt-auto pt-1 border-t border-slate-900/5 shrink-0">
                    <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        seguro === 'privado' ? 'bg-indigo-500/10 text-indigo-700 border border-indigo-200' :
                            seguro === 'mutua' ? 'bg-amber-500/10 text-amber-700 border border-amber-200' :
                                'bg-blue-500/10 text-blue-700 border border-blue-200'
                    }`}>
                        {seguro === 'ninguno' ? 'Privado' : seguro}
                    </span>
                    <MarcaRecordatorio cita={cita} />

                    {/* 🚀 BOTONERA ENRIQUECIDA CON "AUSENTE" */}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            type="button"
                            title="Ver Expediente Clínico"
                            onClick={(e) => { e.stopPropagation(); onVerExpediente && onVerExpediente(paciente.id || paciente._id); }}
                            className="p-1 rounded-md bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 transition-all outline-none"
                        >
                            <ViewIcon sx={{ fontSize: 13 }} />
                        </button>

                        {/* Botón rápido de Ausente (Solo si está pendiente) */}
                        {estado !== 'completada' && estado !== 'ausente' && (
                            <button
                                type="button"
                                title="Marcar como Ausente"
                                onClick={(e) => { e.stopPropagation(); onMarcarAusente && onMarcarAusente(cita); }}
                                className="p-1 rounded-md bg-white hover:bg-rose-50 border border-slate-200 text-rose-600 transition-all outline-none"
                            >
                                <AbsentIcon sx={{ fontSize: 13 }} />
                            </button>
                        )}

                        {/* 🎟️ Cancelar: libera el hueco y NO gasta sesión de bono.
                            Visible también sobre una cita ya marcada como ausente,
                            que es como se le devuelve la sesión al paciente. */}
                        {estado !== 'completada' && estado !== 'cancelada' && (
                            <button
                                type="button"
                                title="Cancelar cita (devuelve la sesión si iba con bono)"
                                onClick={(e) => { e.stopPropagation(); onCancelarCita && onCancelarCita(cita); }}
                                className="p-1 rounded-md bg-white hover:bg-amber-50 border border-slate-200 text-amber-600 transition-all outline-none"
                            >
                                <CancelarCitaIcon sx={{ fontSize: 13 }} />
                            </button>
                        )}

                        {estado !== 'completada' ? (
                            <button
                                type="button"
                                title="Abrir Estación Médica (Atender)"
                                onClick={(e) => { e.stopPropagation(); onAbrirEstacion && onAbrirEstacion(cita); }}
                                className="p-1 rounded-md bg-white hover:bg-slate-100 border border-slate-200 text-blue-600 transition-all outline-none"
                            >
                                <ClinicalIcon sx={{ fontSize: 13 }} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                title="Emitir Factura / Cobrar"
                                onClick={(e) => { e.stopPropagation(); onCobrarConsulta && onCobrarConsulta(cita); }}
                                className="p-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-all shadow-sm outline-none"
                            >
                                <CashIcon sx={{ fontSize: 13 }} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={setDroppableRef}
            role="button"
            tabIndex={temporalidad === 'pasado' ? -1 : 0}
            onClick={(e) => {
                if (temporalidad === 'pasado') {
                    alert('Inconsistencia: No está permitido agendar turnos en bloques horarios del pasado.');
                    return;
                }
                if (onContextMenu) onContextMenu(e, hora, medicoId);
            }}
            onKeyDown={(e) => {
                if (temporalidad === 'pasado') return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (onContextMenu) onContextMenu(e, hora, medicoId);
                }
            }}
            className={`w-full h-full rounded-xl border flex flex-col items-center justify-center transition-all p-2 select-none relative group ${getEstiloSlotLibre(temporalidad)} ${
                isOver ? 'bg-blue-50/50 border-blue-400 border-2 text-blue-400 ring-0' : ''
            }`}
        >
            {temporalidad !== 'pasado' ? (
                <AddIcon
                    className="opacity-0 group-hover:opacity-100 scale-70 group-hover:scale-110 transition-all duration-200 shrink-0"
                    sx={{ fontSize: 20 }}
                />
            ) : null}

            <div className={temporalidad !== 'pasado' ? 'group-hover:opacity-0 transition-opacity absolute' : 'absolute'}>
                {getBadgeSlotLibre(temporalidad)}
            </div>
        </div>
    );
};

export default AgendaSlot;